import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function AirbnbFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  
  // Estados gerais
  const [activeTab, setActiveTab] = useState("feed");
  const [loading, setLoading] = useState(true);
  
  // Estados do Feed
  const [posts, setPosts] = useState([]);
  const [propertyType, setPropertyType] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  
  // Estados de Reserva
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(1);
  const [pin, setPin] = useState("");
  const [guestNotes, setGuestNotes] = useState("");
  const [reserveLoading, setReserveLoading] = useState(false);
  const [reserveError, setReserveError] = useState("");
  const [reserveSuccess, setReserveSuccess] = useState("");
  
  // Minhas Reservas
  const [myReservations, setMyReservations] = useState([]);
  const [myReservationsLoading, setMyReservationsLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  
  // Reservas Recebidas
  const [hostReservations, setHostReservations] = useState([]);
  const [hostReservationsLoading, setHostReservationsLoading] = useState(false);
  
  // Cancelamento
  const [cancelReason, setCancelReason] = useState("");
  const [cancelPin, setCancelPin] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);

  const DAY_MS = 1000 * 60 * 60 * 24;

  const handleEditPost = (post) => {
    setSelectedProperty(null);
    setEditingPost(post);
  };
  
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedProperty(null);
  };

  useEffect(() => {
    if (selectedProperty) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const nextDay = new Date(tomorrow);
      nextDay.setDate(nextDay.getDate() + 1);
      
      setCheckIn(tomorrow.toISOString().split('T')[0]);
      setCheckOut(nextDay.toISOString().split('T')[0]);
      setGuests(1);
      setPin("");
      setGuestNotes("");
      setReserveError("");
      setReserveSuccess("");
    }
  }, [selectedProperty]);

  const propertyTypes = [
    { id: "all", name: "Todos", icon: "🏠" },
    { id: "entire_place", name: "Lugar inteiro", icon: "🏡" },
    { id: "private_room", name: "Quarto privado", icon: "🛏️" },
    { id: "shared_room", name: "Quarto compartilhado", icon: "👥" },
    { id: "hotel_room", name: "Hotel", icon: "🏨" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/airbnb?page=${pageNum}&limit=12`;
      if (propertyType !== "all") url += `&propertyType=${propertyType}`;

      const { data } = await axios.get(url, { 
        headers: { Authorization: `Bearer ${user.token}` } 
      });
      
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching airbnb:", error);
      setLoading(false);
    }
  };

  const fetchMyReservations = async () => {
    try {
      setMyReservationsLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/airbnb/reservations`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setMyReservations(data.reservations || []);
      setMyReservationsLoading(false);
    } catch (error) {
      console.error("Error fetching my reservations:", error);
      setMyReservationsLoading(false);
    }
  };

  const fetchHostReservations = async () => {
    try {
      setHostReservationsLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/airbnb/host/reservations`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setHostReservations(data.reservations || []);
      setHostReservationsLoading(false);
    } catch (error) {
      console.error("Error fetching host reservations:", error);
      setHostReservationsLoading(false);
    }
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 1;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = Math.ceil((end - start) / DAY_MS);
    return diff > 0 ? diff : 1;
  };

  const calculateTotal = () => {
    if (!selectedProperty) return 0;
    const nights = calculateNights();
    const pricePerNight = selectedProperty.airbnbData?.pricePerNight?.amount || 0;
    const cleaningFee = selectedProperty.airbnbData?.cleaningFee || 0;
    const serviceFee = Math.round((pricePerNight * nights) * 0.10);
    return (pricePerNight * nights) + cleaningFee + serviceFee;
  };

  const handleReserve = async () => {
    if (!selectedProperty) return;
    setReserveError("");
    setReserveSuccess("");

    if (!checkIn || !checkOut) {
      setReserveError("Selecione as datas de check-in e check-out");
      return;
    }

    const nights = calculateNights();
    if (nights <= 0) {
      setReserveError("Check-out deve ser depois do check-in");
      return;
    }

    if (guests < 1) {
      setReserveError("Número de hóspedes inválido");
      return;
    }

    if (!/^\d{6}$/.test(pin)) {
      setReserveError("PIN da wallet inválido (6 dígitos)");
      return;
    }

    setReserveLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/airbnb/reservations`,
        {
          postId: selectedProperty._id,
          checkIn,
          checkOut,
          guests,
          pin,
          guestNotes,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data?.success) {
        setReserveSuccess("✅ Reserva confirmada e paga via Wallet Socializa!");
        setTimeout(() => {
          setSelectedProperty(null);
          if (activeTab === "my-reservations") {
            fetchMyReservations();
          }
        }, 2000);
      }
    } catch (error) {
      setReserveError(error.response?.data?.message || "Não foi possível concluir a reserva");
    } finally {
      setReserveLoading(false);
    }
  };

  const handleCancelClick = (reservation) => {
    setReservationToCancel(reservation);
    setCancelReason("");
    setCancelPin("");
    setShowCancelModal(true);
  };

  const handleCancelReservation = async () => {
    if (!reservationToCancel) return;
    
    if (!/^\d{6}$/.test(cancelPin)) {
      alert("PIN da wallet inválido (6 dígitos)");
      return;
    }

    try {
      const { data} = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/airbnb/reservations/${reservationToCancel._id}/cancel`,
        { reason: cancelReason, pin: cancelPin },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data?.success) {
        alert("Reserva cancelada com sucesso!");
        setShowCancelModal(false);
        setReservationToCancel(null);
        
        if (activeTab === "my-reservations") {
          fetchMyReservations();
        } else if (activeTab === "host-reservations") {
          fetchHostReservations();
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao cancelar reserva");
    }
  };

  useEffect(() => {
    if (activeTab === "feed") {
      setPage(1);
      fetchPosts(1);
    } else if (activeTab === "my-reservations") {
      fetchMyReservations();
    } else if (activeTab === "host-reservations") {
      fetchHostReservations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, propertyType]);

  const getStatusBadge = (status) => {
    const badges = {
      pending_payment: { label: "Aguardando", color: "#FFA500" },
      confirmed: { label: "Confirmada", color: "#4CAF50" },
      checked_in: { label: "Check-in", color: "#2196F3" },
      checked_out: { label: "Check-out", color: "#9C27B0" },
      completed: { label: "Concluída", color: "#607D8B" },
      cancelled: { label: "Cancelada", color: "#F44336" },
      refunded: { label: "Estornada", color: "#FF9800" },
    };
    const badge = badges[status] || badges.pending_payment;
    return <span className="status_badge" style={{ backgroundColor: badge.color }}>{badge.label}</span>;
  };

  if (loading && posts.length === 0 && activeTab === "feed") {
    return (
      <div className="airbnb_feed_loading">
        <div className="airbnb_loader"></div>
        <span>Carregando acomodações...</span>
      </div>
    );
  }

  return (
    <div className="airbnb_feed">
      {/* Header com Tabs */}
      <div className="airbnb_header">
        <div className="header_content">
          <h1>🏠 Airbnb</h1>
          <p>Encontre o lugar perfeito para sua próxima viagem</p>
        </div>
        <div className="airbnb_tabs">
          <button
            className={`tab_btn ${activeTab === "feed" ? "active" : ""}`}
            onClick={() => setActiveTab("feed")}
          >
            🔍 Explorar
          </button>
          <button
            className={`tab_btn ${activeTab === "my-reservations" ? "active" : ""}`}
            onClick={() => setActiveTab("my-reservations")}
          >
            📋 Minhas Reservas
          </button>
          <button
            className={`tab_btn ${activeTab === "host-reservations" ? "active" : ""}`}
            onClick={() => setActiveTab("host-reservations")}
          >
            🏡 Reservas Recebidas
          </button>
        </div>
      </div>

      {/* Aba: Feed */}
      {activeTab === "feed" && (
        <>
          {propertyTypes.length > 0 && (
            <div className="simple_filter_bar">
              <div className="filter_buttons">
                {propertyTypes.map((filter) => (
                  <button
                    key={filter.id}
                    className={`filter_btn ${propertyType === filter.id ? "active" : ""}`}
                    onClick={() => setPropertyType(filter.id)}
                  >
                    <span className="filter_icon">{filter.icon}</span>
                    {filter.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {posts.length === 0 ? (
            <div className="airbnb_feed_empty">
              <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
              <h3>Nenhuma acomodação encontrada</h3>
              <p>Seja o primeiro a anunciar!</p>
            </div>
          ) : (
            <div className="airbnb_grid">
              {posts.map((post) => (
                <div key={post._id} className="airbnb_card" onClick={() => setSelectedProperty(post)}>
                  <div className="airbnb_image_container">
                    <img 
                      src={post.airbnbData?.images?.[0] || post.images?.[0]?.url || post.images?.[0] || "/images/default-airbnb.png"} 
                      alt={post.airbnbData?.propertyName}
                    />
                    {post.airbnbData?.rating && (
                      <div className="airbnb_rating">
                        ⭐ {post.airbnbData.rating.toFixed(1)}
                      </div>
                    )}
                  </div>
                  <div className="airbnb_card_content">
                    <div className="airbnb_card_header">
                      <span className="airbnb_type">
                        {propertyTypes.find(t => t.id === post.airbnbData?.propertyType)?.name || "Acomodação"}
                      </span>
                      <div onClick={(e) => e.stopPropagation()}>
                        <FeedPostMenu
                          post={post}
                          onEdit={handleEditPost}
                          onPostDeleted={handlePostDeleted}
                        />
                      </div>
                    </div>
                    <h3>{post.airbnbData?.propertyName || post.text}</h3>
                    <p className="airbnb_location">
                      📍 {post.airbnbData?.city}, {post.airbnbData?.country || "Brasil"}
                    </p>
                    {post.airbnbData?.bedrooms && (
                      <p className="airbnb_details">
                        🛏️ {post.airbnbData.bedrooms} quartos · 
                        🛁 {post.airbnbData.bathrooms || 1} banheiros
                      </p>
                    )}
                    <div className="airbnb_price">
                      <span className="price_value">
                        R$ {(post.airbnbData?.pricePerNight?.amount || 0).toFixed(0)}
                      </span>
                      <span className="price_label">/noite</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <div className="airbnb_load_more">
              <button 
                onClick={() => { 
                  setPage(p => p + 1); 
                  fetchPosts(page + 1); 
                }} 
                disabled={loading}
              >
                {loading ? "Carregando..." : "Ver mais"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Aba: Minhas Reservas */}
      {activeTab === "my-reservations" && (
        <div className="reservations_container">
          <h2>📋 Minhas Reservas</h2>
          {myReservationsLoading ? (
            <div className="reservations_loading">Carregando...</div>
          ) : myReservations.length === 0 ? (
            <div className="reservations_empty">
              <p>Você ainda não tem reservas</p>
            </div>
          ) : (
            <div className="reservations_list">
              {myReservations.map((reservation) => (
                <div key={reservation._id} className="reservation_card">
                  <div className="reservation_image">
                    <img 
                      src={reservation.post?.images?.[0]?.url || reservation.post?.images?.[0] || "/images/default-airbnb.png"} 
                      alt={reservation.propertyName}
                    />
                  </div>
                  <div className="reservation_content">
                    <div className="reservation_header">
                      <h3>{reservation.propertyName}</h3>
                      {getStatusBadge(reservation.status)}
                    </div>
                    <p className="reservation_location">
                      📍 {reservation.city}, {reservation.country}
                    </p>
                    <p className="reservation_dates">
                      📅 {new Date(reservation.checkInDate).toLocaleDateString()} - {new Date(reservation.checkOutDate).toLocaleDateString()}
                    </p>
                    <p className="reservation_guests">
                      👥 {reservation.guests} hóspede{reservation.guests > 1 ? 's' : ''} · {reservation.nights} noite{reservation.nights > 1 ? 's' : ''}
                    </p>
                    <p className="reservation_total">
                      💰 Total: R$ {reservation.totalAmount.toFixed(2)}
                    </p>
                    <div className="reservation_actions">
                      <button onClick={() => setSelectedReservation(reservation)}>
                        Ver Detalhes
                      </button>
                      {["pending_payment", "confirmed"].includes(reservation.status) && (
                        <button 
                          className="btn_cancel"
                          onClick={() => handleCancelClick(reservation)}
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Aba: Reservas Recebidas */}
      {activeTab === "host-reservations" && (
        <div className="reservations_container">
          <h2>🏡 Reservas Recebidas</h2>
          {hostReservationsLoading ? (
            <div className="reservations_loading">Carregando...</div>
          ) : hostReservations.length === 0 ? (
            <div className="reservations_empty">
              <p>Você ainda não recebeu reservas</p>
            </div>
          ) : (
            <div className="reservations_list">
              {hostReservations.map((reservation) => (
                <div key={reservation._id} className="reservation_card">
                  <div className="reservation_image">
                    <img 
                      src={reservation.post?.images?.[0]?.url || reservation.post?.images?.[0] || "/images/default-airbnb.png"} 
                      alt={reservation.propertyName}
                    />
                  </div>
                  <div className="reservation_content">
                    <div className="reservation_header">
                      <h3>{reservation.propertyName}</h3>
                      {getStatusBadge(reservation.status)}
                    </div>
                    <p className="reservation_guest">
                      👤 Hóspede: {reservation.user?.first_name} {reservation.user?.last_name}
                    </p>
                    <p className="reservation_dates">
                      📅 {new Date(reservation.checkInDate).toLocaleDateString()} - {new Date(reservation.checkOutDate).toLocaleDateString()}
                    </p>
                    <p className="reservation_guests">
                      👥 {reservation.guests} hóspede{reservation.guests > 1 ? 's' : ''} · {reservation.nights} noite{reservation.nights > 1 ? 's' : ''}
                    </p>
                    <p className="reservation_total">
                      💰 Valor: R$ {reservation.totalAmount.toFixed(2)}
                    </p>
                    <div className="reservation_actions">
                      <button onClick={() => setSelectedReservation(reservation)}>
                        Ver Detalhes
                      </button>
                      {reservation.status === "confirmed" && (
                        <button 
                          className="btn_cancel"
                          onClick={() => handleCancelClick(reservation)}
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de Reserva */}
      {selectedProperty && (
        <div className="airbnb_modal_overlay" onClick={() => setSelectedProperty(null)}>
          <div className="airbnb_modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal_close" onClick={() => setSelectedProperty(null)}>✕</button>
            
            <div className="modal_gallery">
              <img 
                src={selectedProperty.airbnbData?.images?.[0] || selectedProperty.images?.[0]?.url || selectedProperty.images?.[0] || "/images/default-airbnb.png"} 
                alt={selectedProperty.airbnbData?.propertyName}
              />
            </div>

            <div className="modal_body">
              <h2>{selectedProperty.airbnbData?.propertyName || selectedProperty.text}</h2>
              <p className="modal_location">
                📍 {selectedProperty.airbnbData?.address}, {selectedProperty.airbnbData?.city}
              </p>
              
              <div className="modal_details">
                <span>{selectedProperty.airbnbData?.bedrooms || 1} quartos</span>
                <span>{selectedProperty.airbnbData?.beds || 1} camas</span>
                <span>{selectedProperty.airbnbData?.bathrooms || 1} banheiros</span>
              </div>

              {selectedProperty.airbnbData?.amenities?.length > 0 && (
                <div className="modal_amenities">
                  <h3>Comodidades</h3>
                  <div className="amenities_list">
                    {selectedProperty.airbnbData.amenities.map((amenity, idx) => (
                      <span key={idx} className="amenity_tag">{amenity}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal_reservation">
                <h3>Fazer Reserva (Pagamento via Wallet)</h3>
                
                <div className="form_group">
                  <label>Check-in</label>
                  <input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form_group">
                  <label>Check-out</label>
                  <input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    min={checkIn}
                  />
                </div>

                <div className="form_group">
                  <label>Hóspedes</label>
                  <input
                    type="number"
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value))}
                    min={1}
                    max={selectedProperty.airbnbData?.maxGuests || 10}
                  />
                </div>

                <div className="form_group">
                  <label>Notas (opcional)</label>
                  <textarea
                    value={guestNotes}
                    onChange={(e) => setGuestNotes(e.target.value)}
                    placeholder="Deixe uma mensagem para o anfitrião..."
                    rows={3}
                  />
                </div>

                <div className="price_breakdown">
                  <div className="price_row">
                    <span>R$ {(selectedProperty.airbnbData?.pricePerNight?.amount || 0).toFixed(0)} x {calculateNights()} noites</span>
                    <span>R$ {((selectedProperty.airbnbData?.pricePerNight?.amount || 0) * calculateNights()).toFixed(2)}</span>
                  </div>
                  {selectedProperty.airbnbData?.cleaningFee > 0 && (
                    <div className="price_row">
                      <span>Taxa de limpeza</span>
                      <span>R$ {selectedProperty.airbnbData.cleaningFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="price_row">
                    <span>Taxa de serviço (10%)</span>
                    <span>R$ {(Math.round(((selectedProperty.airbnbData?.pricePerNight?.amount || 0) * calculateNights()) * 0.10)).toFixed(2)}</span>
                  </div>
                  <div className="price_row total">
                    <strong>Total</strong>
                    <strong>R$ {calculateTotal().toFixed(2)}</strong>
                  </div>
                </div>

                <div className="form_group">
                  <label>PIN da Wallet (6 dígitos) *</label>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>

                {reserveError && (
                  <div className="alert alert_error">{reserveError}</div>
                )}

                {reserveSuccess && (
                  <div className="alert alert_success">{reserveSuccess}</div>
                )}

                <button
                  className="btn_reserve"
                  onClick={handleReserve}
                  disabled={reserveLoading}
                >
                  {reserveLoading ? "Processando..." : "Reservar e Pagar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cancelamento */}
      {showCancelModal && reservationToCancel && (
        <div className="airbnb_modal_overlay" onClick={() => setShowCancelModal(false)}>
          <div className="airbnb_modal small" onClick={(e) => e.stopPropagation()}>
            <button className="modal_close" onClick={() => setShowCancelModal(false)}>✕</button>
            <h2>Cancelar Reserva</h2>
            <p>Tem certeza que deseja cancelar esta reserva?</p>
            <p className="warning_text">O valor será estornado para sua wallet.</p>
            
            <div className="form_group">
              <label>Motivo do cancelamento</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Informe o motivo..."
                rows={3}
              />
            </div>

            <div className="form_group">
              <label>PIN da Wallet (6 dígitos) *</label>
              <input
                type="password"
                value={cancelPin}
                onChange={(e) => setCancelPin(e.target.value)}
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <div className="modal_actions">
              <button className="btn_secondary" onClick={() => setShowCancelModal(false)}>
                Voltar
              </button>
              <button className="btn_danger" onClick={handleCancelReservation}>
                Confirmar Cancelamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes da Reserva */}
      {selectedReservation && (
        <div className="airbnb_modal_overlay" onClick={() => setSelectedReservation(null)}>
          <div className="airbnb_modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal_close" onClick={() => setSelectedReservation(null)}>✕</button>
            
            <h2>Detalhes da Reserva</h2>
            
            <div className="reservation_details">
              <div className="detail_row">
                <strong>Propriedade:</strong>
                <span>{selectedReservation.propertyName}</span>
              </div>
              <div className="detail_row">
                <strong>Localização:</strong>
                <span>{selectedReservation.city}, {selectedReservation.country}</span>
              </div>
              <div className="detail_row">
                <strong>Check-in:</strong>
                <span>{new Date(selectedReservation.checkInDate).toLocaleDateString()} às {selectedReservation.checkInTime || "15:00"}</span>
              </div>
              <div className="detail_row">
                <strong>Check-out:</strong>
                <span>{new Date(selectedReservation.checkOutDate).toLocaleDateString()} às {selectedReservation.checkOutTime || "11:00"}</span>
              </div>
              <div className="detail_row">
                <strong>Hóspedes:</strong>
                <span>{selectedReservation.guests}</span>
              </div>
              <div className="detail_row">
                <strong>Noites:</strong>
                <span>{selectedReservation.nights}</span>
              </div>
              <div className="detail_row">
                <strong>Status:</strong>
                {getStatusBadge(selectedReservation.status)}
              </div>
              <div className="detail_row">
                <strong>Total Pago:</strong>
                <span>R$ {selectedReservation.totalAmount.toFixed(2)}</span>
              </div>
              {selectedReservation.guestNotes && (
                <div className="detail_row">
                  <strong>Notas:</strong>
                  <span>{selectedReservation.guestNotes}</span>
                </div>
              )}
            </div>

            <button className="btn_primary" onClick={() => setSelectedReservation(null)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Edit Post Popup */}
      {editingPost && (
        <EditPostPopup
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
